/**
 * Shared `source_context` block for 5 Whys and HMW. Either JTBD-anchored (shows
 * the source JTBD problem statement) or conversation-anchored (shows problem
 * context / grounding / confidence note).
 */

import React from 'react';
import { LabelValueRow } from './components';
import { gapsCount, weightedScore } from './evidence';
import { fieldToJson, parseFields, type StructuredField } from './field';
import { esc, tagSpanHtml } from './html';

export interface SourceContextModel {
  hasJtbd: boolean;
  jtbdFields: StructuredField[]; // when hasJtbd
  problemContext?: string; // when !hasJtbd
  grounding?: string;
  confidenceNote?: string;
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object';
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

export function parseSourceContext(raw: unknown): SourceContextModel {
  if (!isRecord(raw)) return { hasJtbd: false, jtbdFields: [] };
  const hasJtbd = raw.has_jtbd === true;
  const jtbdFields =
    hasJtbd && isRecord(raw.jtbd_problem_statement)
      ? parseFields(raw.jtbd_problem_statement.fields)
      : [];
  return {
    hasJtbd,
    jtbdFields,
    problemContext: str(raw.problem_context),
    grounding: str(raw.grounding),
    confidenceNote: str(raw.confidence_note),
  };
}

export function sourceContextToJson(m: SourceContextModel): Record<string, unknown> {
  if (m.hasJtbd) {
    return { has_jtbd: true, jtbd_problem_statement: { fields: m.jtbdFields.map(fieldToJson) } };
  }
  return {
    has_jtbd: false,
    ...(m.problemContext ? { problem_context: m.problemContext } : {}),
    ...(m.grounding ? { grounding: m.grounding } : {}),
    ...(m.confidenceNote ? { confidence_note: m.confidenceNote } : {}),
  };
}

/** Small "Source JTBD" box shown atop 5 Whys / HMW cards. */
export const SourceContextCard: React.FC<{ model: SourceContextModel }> = ({ model }) => {
  if (model.hasJtbd) {
    const score = weightedScore(model.jtbdFields);
    const gaps = gapsCount(model.jtbdFields);
    return (
      <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:p-5 bg-surface">
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Source JTBD
          </h3>
          <span className="text-xs font-semibold text-emerald-600">{score}%</span>
          {gaps > 0 && (
            <span className="text-xs text-gray-400">
              {gaps} gap{gaps === 1 ? '' : 's'} that need to be fulfilled
            </span>
          )}
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {model.jtbdFields.map((f) => (
            <LabelValueRow key={f.field} label={f.field} values={[f.value]} tag={f.tag} highlight />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:p-5 bg-surface">
      <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Source JTBD
      </h3>
      <p className="mb-3 text-xs text-gray-400">
        Derived from conversation. No JTBD Problem Statement generated yet.
      </p>
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {model.problemContext && (
          <LabelValueRow label="Problem context" values={[model.problemContext]} highlight />
        )}
        {model.grounding && (
          <LabelValueRow label="Grounding" values={[model.grounding]} highlight />
        )}
        {model.confidenceNote && (
          <LabelValueRow label="Confidence" values={[model.confidenceNote]} highlight />
        )}
      </div>
    </div>
  );
};

export function sourceContextToMarkdown(m: SourceContextModel): string {
  if (m.hasJtbd) {
    const rows = m.jtbdFields
      .map((f) => `- **${f.field}**${f.tag ? ` *(${f.tag})*` : ''}: ${f.value}`)
      .join('\n');
    return `### Source JTBD\n${rows}`;
  }
  const parts = [
    '### Source JTBD',
    'Derived from conversation. No JTBD Problem Statement generated yet.',
  ];
  if (m.problemContext) parts.push(`- **Problem context:** ${m.problemContext}`);
  if (m.grounding) parts.push(`- **Grounding:** ${m.grounding}`);
  if (m.confidenceNote) parts.push(`- **Confidence:** ${m.confidenceNote}`);
  return parts.join('\n');
}

export function sourceContextToHtml(m: SourceContextModel): string {
  if (m.hasJtbd) {
    const score = weightedScore(m.jtbdFields);
    const rows = m.jtbdFields
      .map(
        (f) =>
          `<div class="row"><div class="label hl">${esc(f.field)} ${tagSpanHtml(f.tag)}</div><div class="value"><p>${esc(f.value || '—')}</p></div></div>`
      )
      .join('');
    return `<section class="card"><h3>Source JTBD · ${score}%</h3>${rows}</section>`;
  }
  const row = (label: string, v?: string) =>
    v
      ? `<div class="row"><div class="label hl">${esc(label)}</div><div class="value"><p>${esc(v)}</p></div></div>`
      : '';
  return `<section class="card"><h3>Source JTBD</h3>
    <p class="summary">Derived from conversation. No JTBD Problem Statement generated yet.</p>
    ${row('Problem context', m.problemContext)}${row('Grounding', m.grounding)}${row('Confidence', m.confidenceNote)}</section>`;
}
